import { animes } from "@/mocks/animes";
import { characters } from "@/mocks/characters";
import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import type { Anime, Character, NewsItem } from "@/types";

export const sortAnimes = (list: Anime[], sort: string): Anime[] => {
  const copy = [...list];
  if (sort === "newest") return copy.sort((a, b) => b.year - a.year);
  if (sort === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => b.rating * b.year - a.rating * a.year);
};

export const filterAnimes = (
  list: Anime[],
  filters: { category?: string; years?: string[]; rating?: string }
): Anime[] => {
  return list.filter((item) => {
    if (filters.category && filters.category !== "all" && !item.categories.includes(filters.category)) {
      return false;
    }
    if (filters.years && filters.years.length > 0 && !filters.years.includes(String(item.year))) {
      return false;
    }
    if (filters.rating) {
      const min = Number(filters.rating);
      if (item.rating < min) return false;
    }
    return true;
  });
};

export const findAnime = (id?: string): Anime | undefined =>
  animes.find((item) => item.id === id);

export const findCharacter = (id?: string): Character | undefined =>
  characters.find((item) => item.id === id);

export const findNews = (id?: string): NewsItem | undefined =>
  newsList.find((item) => item.id === id);

export const getAnimeCharacters = (animeId: string): Character[] =>
  characters.filter((item) => item.animeId === animeId);

export const getRelatedAnimes = (anime: Anime, limit = 4): Anime[] => {
  const sameCategory = animes.filter(
    (item) => item.id !== anime.id && item.categories.some((c) => anime.categories.includes(c))
  );
  const others = animes.filter(
    (item) => item.id !== anime.id && !sameCategory.some((s) => s.id === item.id)
  );
  return [...sameCategory, ...others].slice(0, limit);
};

export const getRelatedNews = (news: NewsItem, limit = 3): NewsItem[] => {
  const same = newsList.filter((item) => item.id !== news.id && item.category === news.category);
  const others = newsList.filter(
    (item) => item.id !== news.id && !same.some((s) => s.id === item.id)
  );
  return [...same, ...others].slice(0, limit);
};

export const searchAll = (keyword: string) => {
  const q = keyword.trim().toLowerCase();
  if (!q) return { animeResults: [] as Anime[], newsResults: [] as NewsItem[] };
  const animeResults = animes.filter(
    (item) =>
      item.title.toLowerCase().includes(q) ||
      item.titleJa.toLowerCase().includes(q) ||
      item.categories.some((c) => c.toLowerCase().includes(q))
  );
  const newsResults = newsList.filter(
    (item) => item.title.toLowerCase().includes(q) || item.summary.toLowerCase().includes(q)
  );
  return { animeResults, newsResults };
};

export const getCategoryMeta = (key: string) =>
  categories.find((item) => item.key === key);